import { Injectable } from '@angular/core';
import { ChatbotConfig, Environment, EnvironmentCustomerType, EnvironmentServiceInterface } from '@mwe/environment';
import { applyVariablesToTemplateString } from '@mwe/utils';
import { AuthConfig } from 'angular-oauth2-oidc';
import { GATEWAY_AUTH_ROUTE, GATEWAY_ROUTE } from '@mwe/constants';
import { version } from 'package.json';

@Injectable({ providedIn: 'root' })
export class EnvironmentService implements EnvironmentServiceInterface {
  private readonly environment: Environment;

  constructor() {
    const environment = sessionStorage.getItem('environment');
    const substitutedEnvironment = applyVariablesToTemplateString(environment, {
      appHostLocation: `${window.location.origin}`,
      gatewayAuth: `${GATEWAY_ROUTE}/${GATEWAY_AUTH_ROUTE}`,
      appVersion: version,
    });

    // TODO store in cookie storage again serialized environment object after substitution

    this.environment = JSON.parse(substitutedEnvironment);
  }

  getEnvironment(): Environment {
    return this.environment;
  }

  getMaintenanceRefreshIntervall(): number {
    return this.environment.maintenanceRefreshIntervall;
  }

  getChatbotConfig(): ChatbotConfig {
    return this.environment.chatbotConfig;
  }

  getLoggingAPIOBaseURL(): string {
    return this.environment.apiUrl;
  }

  getApiUrl(): string {
    return this.environment.apiUrl;
  }

  getMNRApiUrl(): string {
    return this.environment.mnrApiUrl;
  }

  getCommunityApiUrl(): string {
    return this.environment.communityApiUrl;
  }

  getDsgvoApiUrl(): string {
    return this.environment.dsgvoApiUrl ?? `${this.environment.apiUrl}api/dsgvo/`;
  }

  getPortalCustomerType(): EnvironmentCustomerType {
    return this.environment.customerApp;
  }

  getAuthConfig(): AuthConfig {
    return this.environment.authConfig;
  }

  getRegistrationUri(): string {
    return `${this.environment.authServer?.registrationUri}`;
  }

  getLogoutUri(): string {
    return this.environment.logoutUri;
  }

  getCurrentPortalUri(): string {
    return this.buildPortalUri(`${window.location.origin}`, this.getPortalCustomerType());
  }

  private buildPortalUri(refererUri: string, appName: string): string {
    return refererUri.includes('localhost:42') ? refererUri : `${refererUri}/${appName}`;
  }

  getBusinessPortalUri(): string {
    return this.environment.businessServices.uri;
  }

  getPrivatPortalUri(): string {
    return this.environment.mweServices.uri;
  }

  staticContent(): string {
    return this.environment.staticContent;
  }

  getLogwienProfileUri(): string {
    return `${this.environment.authServer?.profileUri}`;
  }

  getLogwienPartnerName(): string {
    return this.environment.authServer?.partnerName;
  }

  getLogwienLogoutUri(): string {
    return this.environment.authServer?.logoutUri;
  }

  getLogwienLoginFlowUri(): string {
    return `${this.environment.authServer?.loginFlowUri}`;
  }

  getLogwienProfileReferer(): string {
    return this.environment.authServer?.profileReferer;
  }

  getOssUrl(): string {
    return this.environment.ossUri;
  }

  getBskwUri(): string {
    return this.environment.bskwUri;
  }

  getDocumentsUrl() {
    return this.environment.documentUrl;
  }
}
